/**
 * Cockpit Executivo — diretoria (aba Executivo).
 * Responde: "Como está a operação?"
 */
import React from 'react';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Clock,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';
import { formatAnnualBenefit } from '../kaizen/benefitGovernance';
import type { PrimaryBottleneck } from './OpsPrimaryBottleneckCard';
import { EXEC_HERO_CLASS, EXEC_SECTION_LABEL } from './opsExecutiveShared';

interface ExecutiveSystemStatus {
  color: string;
  label: string;
  headline: string;
  description: string;
}

interface ExecutiveOperationalKpi {
  label: string;
  value: string;
  trendColor?: string;
}

interface OpsExecutiveDashboardProps {
  systemStatus: ExecutiveSystemStatus;
  operationalKpis: ExecutiveOperationalKpi[];
  slaGlobal: number;
  slaLabel?: string;
  slaPeriodLabel?: string;
  primaryBottleneck: PrimaryBottleneck | null;
  projectsOverdue: number;
  capacityAvailablePct: number | null;
  throughput: number;
  throughputPeriodLabel?: string;
  teamOverdueCount: number;
  teamPendingCount: number;
  teamInProgressCount: number;
  periodLabel: string;
  trends?: {
    windowDays: number;
    method: string;
    sla: 'improving' | 'worsening' | 'stable' | null;
    throughput: 'improving' | 'worsening' | 'stable' | null;
    leadTime: 'improving' | 'worsening' | 'stable' | null;
  };
  estimatedImpact?: number | null;
  onOpenResults?: () => void;
}

function KpiTile({
  icon: Icon,
  iconClass,
  label,
  value,
  sub,
  title,
  valueClass = 'text-[var(--text-primary)]',
}: {
  icon: React.ElementType;
  iconClass: string;
  label: string;
  value: string;
  sub?: string;
  title?: string;
  valueClass?: string;
}) {
  return (
    <div className={`${EXEC_HERO_CLASS} px-4 py-4`} title={title}>
      <div className="flex items-center gap-2 text-[var(--text-muted)]">
        <Icon size={16} className={iconClass} />
        <span className="text-[9px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <p className={`mt-2 text-[28px] font-bold tabular-nums leading-none ${valueClass}`}>{value}</p>
      {sub && <p className="mt-1 text-[10px] text-[var(--text-secondary)]">{sub}</p>}
    </div>
  );
}

function trendText(direction: 'improving' | 'worsening' | 'stable' | null | undefined): string | null {
  if (direction === 'improving') return '↑ melhorando';
  if (direction === 'worsening') return '↓ piorando';
  if (direction === 'stable') return '→ estável';
  return null;
}

const STATUS_BORDER: Record<string, string> = {
  Problema: 'border-l-red-600',
  Atenção: 'border-l-amber-600',
  'Operação normal': 'border-l-emerald-600',
};

export const OpsExecutiveDashboard: React.FC<OpsExecutiveDashboardProps> = ({
  systemStatus,
  operationalKpis,
  slaGlobal,
  slaLabel = 'SLA concluído · 7 dias',
  slaPeriodLabel = 'Tarefas concluídas no prazo',
  primaryBottleneck,
  projectsOverdue,
  capacityAvailablePct,
  throughput,
  throughputPeriodLabel = 'Últimos 7 dias',
  teamOverdueCount,
  teamPendingCount,
  teamInProgressCount,
  periodLabel,
  trends,
  estimatedImpact = null,
  onOpenResults,
}) => (
  <ExecutiveBody
    systemStatus={systemStatus}
    operationalKpis={operationalKpis}
    slaGlobal={slaGlobal}
    slaLabel={slaLabel}
    slaPeriodLabel={slaPeriodLabel}
    primaryBottleneck={primaryBottleneck}
    projectsOverdue={projectsOverdue}
    capacityAvailablePct={capacityAvailablePct}
    throughput={throughput}
    throughputPeriodLabel={throughputPeriodLabel}
    teamOverdueCount={teamOverdueCount}
    teamPendingCount={teamPendingCount}
    teamInProgressCount={teamInProgressCount}
    periodLabel={periodLabel}
    trends={trends}
    estimatedImpact={estimatedImpact}
    onOpenResults={onOpenResults}
  />
);

function ExecutiveBody({
  systemStatus,
  operationalKpis,
  slaGlobal,
  slaLabel = 'SLA concluído · 7 dias',
  slaPeriodLabel,
  primaryBottleneck,
  projectsOverdue,
  capacityAvailablePct,
  throughput,
  throughputPeriodLabel,
  teamOverdueCount,
  teamPendingCount,
  teamInProgressCount,
  periodLabel,
  trends,
  estimatedImpact,
  onOpenResults,
}: OpsExecutiveDashboardProps) {
  const slaTrend = trendText(trends?.sla);
  const throughputTrend = trendText(trends?.throughput);
  const leadTime = operationalKpis.find((kpi) => kpi.label.startsWith('Lead Time'));
  const wip = operationalKpis.find((kpi) => kpi.label.startsWith('WIP'));

  return (
  <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto px-6 py-5">
    <header>
      <p className={EXEC_SECTION_LABEL}>Desempenho consolidado</p>
      <h1 className="mt-1 text-[22px] font-bold text-[var(--text-primary)]">Como a operação está performando ao longo do período?</h1>
      <p className="mt-1 max-w-3xl text-[12px] text-[var(--text-secondary)]">
        Tendência e leitura gerencial. O Ao Vivo trata a exceção do momento.
      </p>
      <p className="text-[11px] text-[var(--text-muted)]">
        Filtro do ribbon: {periodLabel}. Cada bloco abaixo usa a janela da própria query.
      </p>
    </header>

    <section className="space-y-2">
      <p className={EXEC_SECTION_LABEL}>1. Saúde da operação</p>

    <div className={`${EXEC_HERO_CLASS} border-l-[4px] px-6 py-5 ${STATUS_BORDER[systemStatus.label] ?? 'border-l-[var(--border-default)]'}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-1 h-3 w-3 shrink-0 rounded-full ${systemStatus.color}`} />
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--text-secondary)]">
            {systemStatus.label}
          </p>
          <p className="mt-1 text-[20px] font-bold text-[var(--text-primary)]">{systemStatus.headline}</p>
          <p className="mt-1 text-[11px] leading-relaxed text-[var(--text-secondary)]">{systemStatus.description}</p>
        </div>
      </div>
    </div>
      <KpiTile
        icon={Target}
        iconClass="text-sky-600"
        label={slaLabel}
        value={`${slaGlobal.toFixed(1)}%`}
        sub={[slaPeriodLabel, slaTrend].filter(Boolean).join(' · ')}
        title={slaLabel.startsWith('SLA da fila')
          ? 'Percentual das execuções abertas atualmente que ainda estão dentro do SLA.'
          : 'Percentual das tarefas concluídas nos últimos 7 dias que foram finalizadas dentro do prazo.'}
        valueClass={slaGlobal >= 95 ? 'text-emerald-600' : slaGlobal >= 85 ? 'text-amber-600' : 'text-red-500'}
      />
    </section>

    <section className="space-y-2">
      <p className={EXEC_SECTION_LABEL}>2. Principais riscos</p>
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
      <KpiTile
        icon={BarChart3}
        iconClass="text-red-500"
        label="Gargalo principal"
        value={primaryBottleneck?.stageName ?? 'Nenhum'}
        sub={
          primaryBottleneck
            ? primaryBottleneck.slaSampled === false
              ? `WIP ${primaryBottleneck.wip} · SLA sem amostra · agora`
              : `WIP ${primaryBottleneck.wip} · Impacto SLA ${primaryBottleneck.slaImpactPct.toFixed(0)}% · agora`
            : 'Fluxo sem concentração crítica · agora'
        }
        valueClass="text-[16px] truncate"
      />

      <KpiTile
        icon={AlertTriangle}
        iconClass="text-amber-600"
        label="Projetos atrasados"
        value={String(projectsOverdue)}
        sub="Carteira atual"
        valueClass={projectsOverdue > 0 ? 'text-amber-600' : 'text-emerald-600'}
      />

      <KpiTile
        icon={Clock}
        iconClass="text-orange-600"
        label="Tarefas atrasadas"
        value={String(teamOverdueCount)}
        valueClass={teamOverdueCount > 0 ? 'text-red-500' : 'text-[var(--text-primary)]'}
        sub={`${teamPendingCount} pendentes · ${teamInProgressCount} em andamento · agora`}
      />
    </div>
    </section>

    <section className="space-y-2">
      <p className={EXEC_SECTION_LABEL}>3. Desempenho</p>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiTile
          icon={TrendingUp}
          iconClass="text-indigo-600"
          label="Throughput"
          value={String(throughput)}
          sub={[throughputPeriodLabel, throughputTrend].filter(Boolean).join(' · ')}
        />
        <KpiTile
          icon={Clock}
          iconClass="text-sky-600"
          label="Lead Time"
          value={leadTime?.value ?? '—'}
          sub="Sem série comparável nesta visão"
        />
        <KpiTile
          icon={Activity}
          iconClass="text-[var(--text-muted)]"
          label="WIP"
          value={wip?.value ?? '—'}
          sub="Agora · sem histórico de variação"
        />
        <KpiTile
          icon={Users}
          iconClass="text-violet-600"
          label="Folga de carga"
          value={capacityAvailablePct == null ? '—' : `${capacityAvailablePct.toFixed(0)}%`}
          sub={capacityAvailablePct == null ? 'Sem operadores no escopo' : 'Agora · limite de 8 tarefas ativas'}
          title="Folga da carga simultânea. Inclui operadores do escopo com zero tarefas. Não é capacidade em tarefas/h."
        />
      </div>
    </section>

    <section className="space-y-2">
      <p className={EXEC_SECTION_LABEL}>4. Impacto anual</p>
      <div className={`${EXEC_HERO_CLASS} flex flex-wrap items-end justify-between gap-3 px-4 py-3`}>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Impacto anual estimado ativo</p>
          <p className="mt-1 text-[22px] font-bold tabular-nums text-[var(--text-primary)]">
            {estimatedImpact == null ? '—' : formatAnnualBenefit(estimatedImpact)}
          </p>
          <p className="text-[10px] text-[var(--text-muted)]">Mesma soma de Resultados. Não é ganho do período.</p>
        </div>
        {onOpenResults && (
          <button type="button" onClick={onOpenResults} className="text-[11px] font-semibold text-[var(--nav-active-bar)]">
            Ver em Resultados
          </button>
        )}
      </div>
    </section>

    <div className={`${EXEC_HERO_CLASS} px-5 py-4`}>
      <div className="mb-3 flex items-center gap-2">
        <Activity size={15} className="text-[var(--text-muted)]" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">
          5. Tendência no período
        </span>
      </div>
      <p className="mb-2 text-[10px] text-[var(--text-muted)]" title="Compara a segunda metade da janela selecionada com a primeira metade.">
        {trends?.method ?? 'Tendência no período: compara a segunda metade da janela com a primeira metade.'} Lead time e WIP não têm série de variação nesta visão.
      </p>
      <div className="grid grid-cols-3 gap-3">
        {operationalKpis.map((kpi) => (
          <div
            key={kpi.label}
            className="rounded-sm border border-[var(--border-default)]/60 bg-[var(--bg-elevated)]/40 px-3 py-2.5"
          >
            <p className="text-[8px] font-bold uppercase tracking-wider text-[var(--text-muted)]">{kpi.label}</p>
            <p className={`mt-1 text-[18px] font-bold tabular-nums ${kpi.trendColor || 'text-[var(--text-primary)]'}`}>
              {kpi.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  </div>
  );
}

export default OpsExecutiveDashboard;
